import { useEffect, useRef, useState, type PointerEvent } from "react";

import { Button } from "@buildpy/ui/button";
import { cn } from "@buildpy/ui/cn";

import { createStrokeTracker } from "./signature-stroke";

interface Props {
  // 획을 뗄 때마다 PNG dataURL(획이 너무 짧으면 null) · 지우거나 크기가 바뀌면 null.
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  label?: string;
  className?: string;
  testId?: string;
}

// 픽셀 밀도는 2배까지만 — 서명 PNG 가 서버 상한(200KB)을 넘지 않게.
const MAX_DPR = 2;

// 바탕을 칠하고 획 붓을 맞춘다 — 지우기와 크기 맞춤이 같이 쓴다.
const paintBackground = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const style = getComputedStyle(canvas);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = style.backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const dpr = canvas.width / Math.max(canvas.clientWidth, 1);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.strokeStyle = style.color;
  ctx.lineWidth = 2.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  return ctx;
};

// 손서명 칸 — 포인터(손가락·펜·마우스) 한 벌 · 색은 캔버스 글자색(토큰)을 읽고, 바탕을 함께 칠해 다크 모드에서도 읽히는 PNG 로 낸다.
const SignaturePad = ({ onChange, disabled, label = "서명", className, testId = "signature-pad" }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stroke = useRef(createStrokeTracker());
  const [empty, setEmpty] = useState(true);
  const [short, setShort] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // 크기는 화면에 그려진 뒤 정한다 — 바뀌면 지운 것으로 본다(비율이 어긋난 서명을 보내지 않는다).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const fit = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const width = Math.round(canvas.clientWidth * dpr);
      const height = Math.round(canvas.clientHeight * dpr);
      if (canvas.width === width && canvas.height === height) return;
      canvas.width = width;
      canvas.height = height;
      paintBackground(canvas);
      stroke.current.reset();
      setEmpty(true);
      setShort(false);
      onChangeRef.current(null);
    };
    fit();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(fit);
    observer?.observe(canvas);
    return () => observer?.disconnect();
  }, []);

  const pointOf = (event: PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const ctx = event.currentTarget.getContext("2d");
    if (!ctx) return;
    const { x, y } = pointOf(event);
    if (!stroke.current.down(event.pointerId, { x, y })) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
  };

  const move = (event: PointerEvent<HTMLCanvasElement>) => {
    const ctx = event.currentTarget.getContext("2d");
    if (!ctx) return;
    const { x, y } = pointOf(event);
    if (!stroke.current.move(event.pointerId, { x, y })) return;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!stroke.current.up(event.pointerId)) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setEmpty(false);
    const enough = stroke.current.enough();
    setShort(!enough);
    onChange(enough ? event.currentTarget.toDataURL("image/png") : null);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    paintBackground(canvas);
    stroke.current.reset();
    setEmpty(true);
    setShort(false);
    onChange(null);
  };

  return (
    <div className={cn("flex flex-col gap-y-1.5", className)} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        <Button disabled={disabled || empty} size="sm" type="button" variant="ghost-muted" onClick={clear}>
          지우기
        </Button>
      </div>
      <div className="relative">
        <canvas
          ref={canvasRef}
          aria-label={`${label} 칸 — 손가락이나 펜으로 서명해 주세요`}
          className="block h-40 w-full touch-none rounded-md border border-border bg-overlay-foreground text-overlay"
          role="img"
          onPointerCancel={end}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
        />
        {empty && (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-overlay/50">
            여기에 서명해 주세요
          </p>
        )}
      </div>
      {short && (
        <p className="text-xs text-muted-foreground" role="status">
          서명이 너무 짧아요 — 이어서 그리거나 지우고 다시 해 주세요
        </p>
      )}
    </div>
  );
};

export default SignaturePad;
